import React, { useEffect, useState } from 'react';
import { findIndex } from 'lodash';

import { Checkbox, Input, MaskInput, Select } from 'modules/shared/Forms';
import TitleUndescore from 'modules/shared/TitleUndescore';
import { Peripherals } from 'entities/Peripherals/interfaces';
import { trims } from 'utils';
import styles from '../SettingsSMSCard/SettingsSMSCard.module.scss';

type Props = {
  idx: number;
  value: any;
  peripherals: Peripherals[] | null;
  onChange: (e: React.ChangeEvent<any>) => void;
};

const SecurityCard: React.FC<Props> = ({ value, idx, peripherals, onChange }) => {
  const indexes = {
    ImageScanner: 2,
    Scales: 3,
    FaceID: 4,
  };

  const fieldName = `securityGates[${idx}].peripheralRoles`;

  const findScalesEntrance = () =>
    findIndex(
      value.peripheralRoles,
      (i: any) => i.peripheralHardwareId === indexes.Scales && trims(i.processName).toLocaleLowerCase() === 'entrance',
    );

  const findIdxScalesExit = () =>
    findIndex(
      value.peripheralRoles,
      (i: any) => i.peripheralHardwareId === indexes.Scales && trims(i.processName).toLocaleLowerCase() === 'exit',
    );

  const [idxImageScanner, setIdxImageScanner] = useState<number>();
  const [idxFaceID, setIdxFaceID] = useState<number>();
  const [idxScalesEntrance, setIdxScalesEntrance] = useState<number>(findScalesEntrance());
  const [idxScalesExit, setIdxScalesExit] = useState<number>(findIdxScalesExit);

  const findImageScanner = () =>
    findIndex(value.peripheralRoles, (i: any) => i.peripheralHardwareId === indexes.ImageScanner);

  const findFaceID = () =>
    findIndex(
      value.peripheralRoles,
      (i: any) => i.peripheralHardwareId === indexes.FaceID && trims(i.processName) === 'AuthUser',
    );

  function isBase64(str: string) {
    const regex = /^(?:[A-Za-z\d+/]{4})*(?:[A-Za-z\d+/]{2}==|[A-Za-z\d+/]{3}=)?$/;
    return regex.test(str);
  }

  // eslint-disable-next-line arrow-body-style
  const decodedPeripheralRoles = () => {
    // @ts-ignore
    return value.peripheralRoles.map((role) => {
      if (role.peripheralUrl && isBase64(role.peripheralUrl)) {
        return {
          ...role,
          peripheralUrl: atob(role.peripheralUrl),
        };
      }
      return role;
    });
  };

  useEffect(() => {
    setIdxImageScanner(findImageScanner());
    setIdxFaceID(findFaceID());
    setIdxScalesEntrance(findScalesEntrance());
    setIdxScalesExit(findIdxScalesExit());
    decodedPeripheralRoles();
  }, []);

  return (
    <div className="my-5">
      <TitleUndescore title={`Пост охорони "${value.name}"`} />
      <div className="pl-4">
        <TitleUndescore title="Сканер документів" className="small" />
        <Select
          name={`${fieldName}[${idxImageScanner}].peripheralHardwareId`}
          label="Модель сканера"
          onChange={onChange}
          className="customSelect mb-2"
        >
          {peripherals
            ? peripherals.map((l: any) => {
                if (l.id !== indexes.ImageScanner) return;

                return (
                  <option key={l.id} value={l.id}>
                    {l.model} {l.protocol}
                  </option>
                );
              })
            : null}
        </Select>
        <Input
          onChange={onChange}
          label="Точка підключення"
          name={`${fieldName}[${idxImageScanner}].peripheralUrl`}
          className="customSelect"
        />
      </div>

      <div className="pl-4">
        <TitleUndescore title="Face ID" className="small" />
        <Select
          name={`${fieldName}[${idxFaceID}].peripheralHardwareId`}
          label="Модель камери"
          onChange={onChange}
          className="customSelect mb-2"
        >
          {peripherals
            ? peripherals.map((l: any) => {
                if (l.id !== indexes.FaceID) return;

                return (
                  <option key={l.id} value={l.id}>
                    {l.model} {l.protocol}
                  </option>
                );
              })
            : null}
        </Select>
        <Input
          onChange={onChange}
          label="Точка підключення 'Auth User'"
          isRequiredAtStart
          className="customSelect"
          name={`${fieldName}[${idxFaceID}].peripheralUrl`}
        />
      </div>

      <div className="pl-4">
        <TitleUndescore title="Ваги автомобільні" className="small" />
        <Select
          name={`${fieldName}[${idxScalesEntrance}].peripheralHardwareId`}
          label="Модель вагів"
          onChange={onChange}
          className="customSelect mb-2"
        >
          {peripherals
            ? peripherals.map((l: any) => {
                if (l.id !== indexes.Scales) return;

                return (
                  <option key={l.id} value={l.id}>
                    {l.model} {l.protocol}
                  </option>
                );
              })
            : null}
        </Select>
        <Input
          onChange={onChange}
          label="Точка підключення до вагів в'їзду"
          className="customSelect mb-2"
          isRequiredAtStart
          name={`${fieldName}[${idxScalesEntrance}].peripheralUrl`}
        />
        <Checkbox
          className={styles.checkbox}
          onChange={onChange}
          label="&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;ручний режим отримання вагів в'їзду"
          name={`${fieldName}[${idxScalesEntrance}].isManualGetWeight`}
          value={value.peripheralRoles[idxScalesEntrance]?.isManualGetWeight}
        />
        <Input
          onChange={onChange}
          label="Точка підключення до вагів виїзду"
          isRequiredAtStart
          className="customSelect mb-2"
          name={`${fieldName}[${idxScalesExit}].peripheralUrl`}
        />
        <Checkbox
          className={styles.checkbox}
          onChange={onChange}
          label="&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;ручний режим отримання вагів виїзду"
          name={`${fieldName}[${idxScalesExit}].isManualGetWeight`}
          value={value.peripheralRoles[idxScalesExit]?.isManualGetWeight}
        />
        <MaskInput
          name={`${fieldName}[${idxScalesExit}].allowedExitTolerance`}
          label="Толеранс виїзду, фактична вага, %"
          placeholder="X.XX"
          className="customSelect mb-2"
          maskPlaceholder=""
          mask="9.99"
          onChange={onChange}
          // @ts-ignore
          value={value.peripheralRoles[idxScalesExit]?.allowedExitTolerance}
        />
        <MaskInput
          name={`${fieldName}[${idxScalesExit}].allowedExitTheoreticalTolerance`}
          label="Толеранс виїзду, теоретична вага, %"
          placeholder="X.XX"
          className="customSelect mb-2"
          maskPlaceholder=""
          mask="9.99"
          onChange={onChange}
          // @ts-ignore
          value={value.peripheralRoles[idxScalesExit]?.allowedExitTheoreticalTolerance}
        />
        <MaskInput
          name={`${fieldName}[${idxScalesExit}].allowableToleranceWithoutLoading`}
          label="Толеранс виїзду без завантаження, %"
          placeholder="X.XX"
          className="customSelect mb-2"
          maskPlaceholder=""
          mask="9.99"
          onChange={onChange}
          // @ts-ignore
          value={value.peripheralRoles[idxScalesExit]?.allowableToleranceWithoutLoading}
        />
      </div>
    </div>
  );
};

export default SecurityCard;
